<template>
  <section class="sidebar">
    <!-- Mobile Backdrop Overlay -->
    <div
      class="sidebar_overlay"
      id="sidebarOverlay"
      @click="closeMobileMenu"
    ></div>

    <section class="fullScreen" id="sidebar" v-if="!isSmallScreen">
      <!-- ============================== Big Screen Sidebar -->
      <v-navigation-drawer
        class="main_sidebar"
        permanent
        :width="270"
        :expand-on-hover="false"
      >
        <!-- Upper Section -->
        <div class="top_logo">
          <div class="logo">
            <router-link to="/">
              <img
                class="img-fluid"
                width="150"
                v-if="current_theme == 'light'"
                src="@/assets/images/icons/logo.png"
              />
              <img
                width="150"
                class="img-fluid"
                v-else
                src="@/assets/images/icons/logo.png"
              />
            </router-link>
          </div>
        </div>

        <!-- Quick Menu Search -->
        <div class="sidebar_search_wrapper">
          <div class="search_input_box">
            <i class="fas fa-search search_icon"></i>
            <input
              type="text"
              v-model="searchQuery"
              :placeholder="
                lang == 'ar' ? 'بحث في القوائم...' : 'Search menus...'
              "
              class="sidebar_search_input"
            />
            <i
              v-if="searchQuery"
              @click="searchQuery = ''"
              class="fas fa-times clear_icon"
            ></i>
          </div>
        </div>

        <v-list class="sidebar_menu_list">
          <template>
            <v-list-item
              to="/"
              v-if="user.usertype != 'admin'"
              class="home_item"
            >
              <v-icon class="item_icon">fas fa-home</v-icon>
              <v-list-item-title>{{ $t("navs.home") }}</v-list-item-title>
            </v-list-item>
          </template>

          <template v-for="(item, idx) in displayedSidebar">
            <div
              class="creative_header"
              v-if="item.label"
              :key="item.id || 'group-' + idx"
            >
              <div class="header_content">
                <i
                  :class="item.icon || 'fas fa-layer-group'"
                  class="group_icon"
                ></i>
                <span class="group_title">{{ item.label }}</span>
              </div>
              <span
                class="group_badge"
                v-if="item.categories && item.categories.length"
              >
                {{ item.categories.length }}
              </span>
            </div>

            <template v-for="sub_item in item.categories">
              <v-list-item
                v-if="sub_item.key == 'general'"
                :key="sub_item.id || sub_item.title"
                :to="`/${sub_item.permissions[0].url}`"
                class="single_menu_item"
              >
                <v-icon class="item_icon">{{ sub_item.icon }}</v-icon>
                <v-list-item-title>{{ sub_item.title }}</v-list-item-title>
              </v-list-item>

              <!-- If DropDown -->
              <v-list-group
                :key="sub_item.uuid || sub_item.title"
                v-else
                :class="{
                  activeDropDown: sub_item.active,
                }"
                class="dropdown_menu_group"
                v-model="sub_item.active"
                :prepend-icon="sub_item.icon"
              >
                <template v-slot:activator>
                  <v-list-item-content>
                    <v-list-item-title>{{ sub_item.title }}</v-list-item-title>
                  </v-list-item-content>
                </template>
                <v-list-item
                  v-for="child in sub_item.permissions"
                  :to="`/${child.url}`"
                  :key="child.id || child.url"
                  class="sub_menu_item"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      <i class="fas fa-circle sub_dot"></i>
                      {{ child.title }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-group>
            </template>
          </template>
        </v-list>

        <div class="country_select_card mx-2" v-if="countries.length">
          <label class="form-label">
            <i class="fas fa-globe-americas me-1"></i>
            {{ $t("forms.labels.country") }}
          </label>

          <multiselect
            v-model="country"
            @input="changeCountry"
            :options="countries"
            label="name"
            track-by="id"
            :placeholder="$t('forms.labels.selectCountry')"
            :searchable="true"
            :allow-empty="true"
            :show-labels="false"
            open-direction="top"
          >
            <template v-slot:caret="{ toggle }">
              <div
                class="multiselect_custom_caret"
                @click.prevent.stop="toggle"
              >
                <i class="fas fa-chevron-down"></i>
              </div>
            </template>
          </multiselect>
        </div>
      </v-navigation-drawer>
    </section>

    <!-- ============================== Small Screen Sidebar -->
    <aside
      v-if="isSmallScreen"
      class="smallScreenSidebar main_sidebar"
      ref="smallSidebar"
      @click.stop
    >
      <span @click="closeMobileMenu" class="close">
        <i class="fas fa-times"></i>
      </span>

      <!-- Upper Section -->
      <div class="top_logo">
        <div class="logo">
          <router-link to="/" @click.native="closeMobileMenu">
            <img
              class="img-fluid"
              width="150"
              v-if="current_theme == 'light'"
              src="@/assets/images/icons/logo.png"
            />
            <img
              width="150"
              class="img-fluid"
              v-else
              src="@/assets/images/icons/logo.png"
            />
          </router-link>
        </div>
      </div>

      <div class="sidebar_search_wrapper">
        <div class="search_input_box">
          <i class="fas fa-search search_icon"></i>
          <input
            type="text"
            v-model="searchQuery"
            :placeholder="
              lang == 'ar' ? 'بحث في القوائم...' : 'Search menus...'
            "
            class="sidebar_search_input"
          />
          <i
            v-if="searchQuery"
            @click="searchQuery = ''"
            class="fas fa-times clear_icon"
          ></i>
        </div>
      </div>

      <v-list class="sidebar_menu_list">
        <v-list-item to="/" class="home_item" @click="closeMobileMenu">
          <v-icon class="item_icon">fas fa-home</v-icon>
          <v-list-item-title>{{ $t("navs.home") }}</v-list-item-title>
        </v-list-item>

        <template v-for="(item, idx) in displayedSidebar">
          <div
            class="creative_header"
            v-if="item.label"
            :key="item.id || 'mobile-group-' + idx"
          >
            <div class="header_content">
              <i
                :class="item.icon || 'fas fa-layer-group'"
                class="group_icon"
              ></i>
              <span class="group_title">{{ item.label }}</span>
            </div>
            <span
              class="group_badge"
              v-if="item.categories && item.categories.length"
            >
              {{ item.categories.length }}
            </span>
          </div>

          <template v-for="sub_item in item.categories">
            <v-list-item
              v-if="sub_item.key == 'general'"
              :key="sub_item.id || sub_item.title"
              :to="`/${sub_item.permissions[0].url}`"
              class="single_menu_item"
              @click="closeMobileMenu"
            >
              <v-icon class="item_icon">{{ sub_item.icon }}</v-icon>
              <v-list-item-title>{{ sub_item.title }}</v-list-item-title>
            </v-list-item>

            <!-- If DropDown -->
            <v-list-group
              v-else
              :key="sub_item.uuid || sub_item.title"
              :class="{
                activeDropDown: sub_item.active,
              }"
              class="dropdown_menu_group"
              v-model="sub_item.active"
              :prepend-icon="sub_item.icon"
            >
              <template v-slot:activator>
                <v-list-item-content>
                  <v-list-item-title>{{ sub_item.title }}</v-list-item-title>
                </v-list-item-content>
              </template>

              <template>
                <v-list-item
                  v-for="child in sub_item.permissions"
                  :to="`/${child.url}`"
                  :key="child.id || child.url"
                  class="sub_menu_item"
                  @click="closeMobileMenu"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      <i class="fas fa-circle sub_dot"></i>
                      {{ child.title }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-list-group>
          </template>
        </template>
      </v-list>

      <div
        class="country_select_card mx-2 mt-auto mb-2"
        v-if="countries.length"
      >
        <label class="form-label">
          <i class="fas fa-globe-americas me-1"></i>
          {{ $t("forms.labels.country") }}
        </label>

        <multiselect
          v-model="country"
          @input="changeCountry"
          :options="countries"
          label="name"
          track-by="id"
          :placeholder="$t('forms.labels.selectCountry')"
          :searchable="true"
          :allow-empty="true"
          :show-labels="false"
          open-direction="top"
        >
          <template v-slot:caret="{ toggle }">
            <div class="multiselect_custom_caret" @click.prevent.stop="toggle">
              <i class="fas fa-chevron-down"></i>
            </div>
          </template>
        </multiselect>
      </div>
    </aside>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import { USER_TYPES, isSupportUser } from "@/helpers/roles.js";

export default {
  data() {
    return {
      menueMinWidth: true,
      sidebar: [],
      searchQuery: "",
      country: JSON.parse(localStorage.getItem("zoz_dash_country")) || null,
    };
  },

  methods: {
    changeCountry(e) {
      if (e) {
        console.log("country", e);
        localStorage.setItem("zoz_dash_country", JSON.stringify(e));
        this.$router.push("/");
        setTimeout(() => window.location.reload(), 300);
      } else {
        localStorage.removeItem("zoz_dash_country");
        this.$router.push("/");
        setTimeout(() => window.location.reload(), 300);
      }
    },
    toggleSideBarFullWidth() {
      this.menueMinWidth = !this.menueMinWidth;
      this.$store.dispatch("sideNav_module/onResize");
    },

    toggleMenu() {
      const sidebar =
        this.$refs.smallSidebar ||
        document.querySelector(".smallScreenSidebar");
      const overlay = document.getElementById("sidebarOverlay");
      if (sidebar) sidebar.classList.toggle("active");
      if (overlay) overlay.classList.toggle("active");
    },

    closeMobileMenu() {
      const sidebar =
        this.$refs.smallSidebar ||
        document.querySelector(".smallScreenSidebar");
      const overlay = document.getElementById("sidebarOverlay");
      if (sidebar) sidebar.classList.remove("active");
      if (overlay) overlay.classList.remove("active");
    },

    changeTheme(value) {
      this.$store.dispatch("theme_module/changeTheme", value);
      if (value == "light") {
        this.$vuetify.theme.dark = false;
      } else {
        this.$vuetify.theme.dark = true;
      }
      this.closeMobileMenu();
    },

    changeLang(value) {
      this.$store.dispatch("lang_module/switchLang", value);
      this.closeMobileMenu();
    },

    // Logs & app versions entries are exclusive to the support user type
    filterSupportOnlyItems(groups) {
      const isSupport = isSupportUser(this.$store);

      return groups
        .map((group) => {
          const categories = (group.categories || []).filter(
            (cat) => isSupport || !cat.support_only
          );
          return { ...group, categories };
        })
        .filter(
          (group) =>
            (isSupport || !group.support_only) && group.categories.length > 0
        );
    },

    manipulateTheSideBar() {
      // Convert 'JSON' Object to JS Object
      const Json_Sidebar = this.filterSupportOnlyItems(
        JSON.parse(JSON.stringify(this.$t("sidebar")))
      );

      Json_Sidebar.forEach((el) => {
        if (el.categories) {
          el.categories.forEach((subEl) => {
            if (subEl.permissions && subEl.permissions.length > 0) {
              const firstUrl =
                "/" + subEl.permissions[0].url.replace(/^\/+/, "");
              subEl.active = this.$route.path.startsWith(
                firstUrl.split("?")[0]
              );
            }
          });
        }
      });

      this.sidebar = Json_Sidebar;
    },
    getSideBar() {
      const localSidebar = this.filterSupportOnlyItems(
        JSON.parse(JSON.stringify(this.$t("sidebar")))
      );

      // 1. Build a Set of allowed URLs from Vuex (populated from API after login)
      const raw = this.permissionsData;
      const allowedUrls = new Set();

      (Array.isArray(raw) ? raw : []).forEach((p) => {
        const pUrl = (p.url || "").replace(/^\/+/, "").trim();
        const pRoute = (p.front_route_name || "").replace(/^\/+/, "").trim();

        if (pUrl) allowedUrls.add(pUrl);
        if (pRoute) allowedUrls.add(pRoute);

        if (pUrl && pRoute && pUrl.includes("/")) {
          const action = pUrl.split("/").slice(1).join("/");
          allowedUrls.add(`${pRoute}/${action}`);
        }
      });

      if (allowedUrls.size === 0) {
        this.sidebar = localSidebar;
        return;
      }

      // 2. Filter: keep only permissions whose url is in allowedUrls
      const filteredSidebar = localSidebar
        .map((group) => {
          const categories = (group.categories || [])
            .map((cat) => {
              const permissions = (cat.permissions || []).filter((perm) => {
                const url = (perm.url || "").replace(/^\/+/, "").trim();
                return allowedUrls.has(url);
              });
              return { ...cat, permissions };
            })
            .filter((cat) => cat.permissions.length > 0);

          return { ...group, categories };
        })
        .filter((group) => group.categories.length > 0);

      // 3. Mark active dropdowns
      filteredSidebar.forEach((group) => {
        group.categories.forEach((cat) => {
          if (cat.permissions && cat.permissions.length) {
            const firstUrl = "/" + cat.permissions[0].url.replace(/^\/+/, "");
            cat.active = this.$route.path.startsWith(firstUrl.split("?")[0]);
          }
        });
      });

      this.sidebar = filteredSidebar;
    },
  },

  computed: {
    ...mapGetters({
      current_theme: "theme_module/current_theme",
      lang: "lang_module/lang",
      user: "auth_module/currentUser",
      countries: "countries",
      permissionsData: "permissions",
      isSmallScreen: "sideNav_module/isSmallScreen",
    }),

    // superadmin & support see every operational menu
    hasFullOpsAccess() {
      const userType = (
        this.user.usertype ||
        localStorage.getItem("zoz_dash_User_Type") ||
        ""
      )
        .toString()
        .trim()
        .toLowerCase();
      return (
        userType === USER_TYPES.SUPER_ADMIN || userType === USER_TYPES.SUPPORT
      );
    },

    displayedSidebar() {
      if (!this.searchQuery) return this.sidebar;
      const q = this.searchQuery.toLowerCase().trim();
      return this.sidebar
        .map((group) => {
          const matchingCategories = (group.categories || []).filter((cat) => {
            const catTitle = (cat.title || "").toLowerCase();
            const hasCatMatch = catTitle.includes(q);
            const hasPermMatch = (cat.permissions || []).some(
              (p) =>
                (p.title || "").toLowerCase().includes(q) ||
                (p.url || "").toLowerCase().includes(q)
            );
            return hasCatMatch || hasPermMatch;
          });
          const groupLabelMatch = (group.label || "").toLowerCase().includes(q);
          if (groupLabelMatch || matchingCategories.length > 0) {
            return {
              ...group,
              categories:
                matchingCategories.length > 0
                  ? matchingCategories
                  : group.categories,
            };
          }
          return null;
        })
        .filter(Boolean);
    },
  },

  watch: {
    permissionsData(newVal) {
      if (newVal && newVal.length > 0 && !this.hasFullOpsAccess) {
        this.getSideBar();
      }
    },
    // Widening the window past the mobile breakpoint must not leave the
    // drawer (and its dimmed overlay) open on top of the desktop sidebar
    isSmallScreen(isSmall) {
      if (!isSmall) this.closeMobileMenu();
    },
    $route() {
      this.closeMobileMenu();
      if (this.hasFullOpsAccess) {
        this.manipulateTheSideBar();
      } else {
        this.getSideBar();
      }
    },
    // Rebuild the menus as soon as the user type is known / refreshed
    "user.usertype"() {
      if (this.hasFullOpsAccess) {
        this.manipulateTheSideBar();
      } else {
        this.getSideBar();
      }
    },
  },

  created() {
    this.control = this.$permission.setControls(this.$route.path);
    if (this.hasFullOpsAccess) {
      this.manipulateTheSideBar();
    } else {
      this.getSideBar();
    }
  },
};
</script>

<style lang="scss">
// .main_sidebar.v-navigation-drawer {
//   width: 280px !important;
// }
</style>
